import type { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "../design/Button";
import { Dialog } from "./Dialog";

// Confirm-an-action dialog. Used by every delete/unpin/remove flow.
// Defaults: confirm = destructive (red), cancel = neutral. Pass
// `destructive={false}` for non-destructive confirmations.

interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  message?: ReactNode;
  confirmLabel?: ReactNode;
  cancelLabel?: ReactNode;
  destructive?: boolean;
  // Testanker (UI_CONVENTIONS → Test anchors): landet auf dem <dialog>; die
  // beiden Knöpfe leiten `-confirm` und `-cancel` davon ab.
  id?: string;
  confirmTestId?: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  destructive = true,
  id,
  confirmTestId,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} id={id} dismiss="explicit" onClose={onCancel} title={title}>
      {message ? <div className="mb-6 text-smycles-body text-ink">{message}</div> : null}
      <div className="flex items-center justify-end gap-2">
        <Button id={id ? `${id}-cancel` : undefined} variant="secondary" onClick={onCancel}>
          {cancelLabel ?? (
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          )}
        </Button>
        <Button
          id={id ? `${id}-confirm` : undefined}
          variant={destructive ? "destructive" : "primary"}
          onClick={() => void onConfirm()}
          data-testid={confirmTestId}
        >
          {confirmLabel ?? (
            <FormattedMessage id="common.confirm" defaultMessage="Confirm" />
          )}
        </Button>
      </div>
    </Dialog>
  );
}
